/* The Contract block on a solids (Cargo) recap, classic look. Sits under the
 * recap's Contract No. field.
 *
 * Client checklist: "Contract Creation from Sales & Purchase Recap - Allow
 * standard contract to be generated from approved Sales & Purchase Recap like
 * in fuels. Templates to come, structure can be prepared - External / Uploaded
 * Contracts, option to send to Legal - for review HMS as well".
 *
 *  - No contract yet, recap saved: "Create Contract" opens a pop-up to
 *    generate the standard contract or upload an external one.
 *  - Unsaved recap: says to save it first.
 *  - Contract exists: C-number, status selector (In progress / Final /
 *    Signed), the Legal review state, the contract files to download,
 *    Send to Legal, and Open contract when the Legal page will open for them.
 *
 * State and server calls live in useRecapContract; the server re-checks every
 * action.
 *
 * IMT copy of HMS f9c63e53: the status picker is a plain labelled select and
 * the pop-up takes IMT HMSModal's width (IMT has no ui/fields, no modal sizes).
 */
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useHistory } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowRight,
  faDownload,
  faFileSignature,
  faPaperPlane,
  faSpinner,
} from '@fortawesome/free-solid-svg-icons';

import HMSButton from '../ui/HMSButton';
import HMSModal from '../ui/HMSModal';
import { CONTRACT_STATUS_OPTIONS } from '../../services/legalContractService';
import useRecapContract, { CONTRACT_FILE_ACCEPT, reviewLine } from './useRecapContract';
import './RecapContractBlock.css';

export function CreateContractModal({ isOpen, busy, error, onClose, onCreate, renderActions }) {
  const [mode, setMode] = useState('generate');
  const [file, setFile] = useState(null);
  const canSubmit = mode === 'generate' || Boolean(file);

  const submit = async () => {
    const done = await onCreate(mode, mode === 'upload' ? file : null);
    if (done) {
      setMode('generate');
      setFile(null);
    }
  };

  // Closing forgets the choice: the file input comes back empty on reopen,
  // so a kept file would be sent without showing as chosen.
  const close = () => {
    setMode('generate');
    setFile(null);
    onClose();
  };

  const footer = renderActions
    ? renderActions({ busy, canSubmit, submit, onClose: close })
    : (
      <>
        <HMSButton variant="secondary" onClick={close} disabled={busy}>Cancel</HMSButton>
        <HMSButton variant="primary" onClick={submit} disabled={busy || !canSubmit}>
          {busy ? 'Creating...' : 'Create Contract'}
        </HMSButton>
      </>
    );

  return (
    <HMSModal
      isOpen={isOpen}
      onClose={close}
      title="Create Contract"
      width={480}
      busy={busy}
      showClose
      footer={footer}
      className="recap-contract-modal"
    >
      <p className="rcm-intro">
        The contract gets the next contract number, which is also written on this recap.
      </p>
      <fieldset className="rcm-choices">
        <legend className="rcm-legend">Contract document</legend>
        <label className="rcm-choice">
          <input
            type="radio"
            name="recap-contract-mode"
            value="generate"
            checked={mode === 'generate'}
            onChange={() => setMode('generate')}
            disabled={busy}
          />
          <span>
            <strong>Generate standard contract</strong>
            <span className="rcm-help">
              Built from this recap. The standard terms are a placeholder until Legal supplies the template.
            </span>
          </span>
        </label>
        <label className="rcm-choice">
          <input
            type="radio"
            name="recap-contract-mode"
            value="upload"
            checked={mode === 'upload'}
            onChange={() => setMode('upload')}
            disabled={busy}
          />
          <span>
            <strong>Upload external contract</strong>
            <span className="rcm-help">A contract drawn up outside IMT. PDF, Word, image or text, up to 25 MB.</span>
          </span>
        </label>
      </fieldset>
      {mode === 'upload' && (
        <label className="rcm-file">
          <span className="rcm-legend">Contract file</span>
          <input
            type="file"
            accept={CONTRACT_FILE_ACCEPT}
            aria-label="Contract file"
            onChange={(e) => setFile(e.target.files && e.target.files[0] ? e.target.files[0] : null)}
            disabled={busy}
          />
        </label>
      )}
      {error && <p className="rcm-error" role="alert">{error}</p>}
    </HMSModal>
  );
}

CreateContractModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  busy: PropTypes.bool,
  error: PropTypes.string,
  onClose: PropTypes.func.isRequired,
  onCreate: PropTypes.func.isRequired,
  renderActions: PropTypes.func,
};

const RecapContractBlock = ({ recapId, summary, onContractNumber }) => {
  const history = useHistory();
  const {
    contract, busy, error, notice, create, changeStatus, sendToLegal, download, clearError,
  } = useRecapContract({ recapId, initialSummary: summary, onContractNumber });
  const [creating, setCreating] = useState(false);

  const handleCreate = async (mode, file) => {
    const made = await create(mode, file);
    if (made) setCreating(false);
    return made;
  };

  return (
    <div className="recap-contract-block" data-testid="recap-contract-block">
      <div className="rcb-title">
        <FontAwesomeIcon icon={faFileSignature} />
        Contract
      </div>

      {!recapId && (
        <p className="rcb-muted">Save the recap first, then create its contract.</p>
      )}

      {recapId && !contract && (
        <div className="rcb-empty">
          <span className="rcb-muted">No contract yet.</span>
          <HMSButton variant="secondary" size="compact" onClick={() => { clearError(); setCreating(true); }}>
            Create Contract
          </HMSButton>
        </div>
      )}

      {contract && (
        <>
          <div className="rcb-head">
            <span className="rcb-number">{contract.contract_number}</span>
            {contract.origin_display && <span className="rcb-origin">{contract.origin_display}</span>}
          </div>
          <div className="rcb-status">
            <label className="rcb-label" htmlFor={`recap-contract-status-${contract.id}`}>Contract status</label>
            <select
              id={`recap-contract-status-${contract.id}`}
              value={contract.status}
              onChange={(e) => changeStatus(e.target.value)}
              disabled={!contract.can_manage || busy === 'status'}
            >
              {CONTRACT_STATUS_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
          <p className="rcb-review">
            <span className="rcb-label">Legal review:</span> {reviewLine(contract)}
          </p>
          {contract.files && contract.files.length > 0 && (
            <ul className="rcb-files">
              {contract.files.map((file) => (
                <li key={file.id}>
                  <button
                    type="button"
                    className="rcb-file"
                    onClick={() => download(file)}
                    disabled={busy === file.id}
                    title="Download"
                  >
                    <FontAwesomeIcon icon={busy === file.id ? faSpinner : faDownload} spin={busy === file.id} />
                    {file.original_filename}
                  </button>
                </li>
              ))}
            </ul>
          )}
          <div className="rcb-actions">
            {contract.can_manage && contract.legal_review_status !== 'pending' && (
              <HMSButton variant="secondary" size="compact" onClick={sendToLegal} disabled={busy === 'send'}>
                <FontAwesomeIcon icon={faPaperPlane} /> {busy === 'send' ? 'Sending...' : 'Send to Legal'}
              </HMSButton>
            )}
            {contract.can_open && (
              <HMSButton
                variant="ghost"
                size="compact"
                onClick={() => history.push(`/dashboard/legal/contracts/${contract.id}`)}
              >
                Open contract <FontAwesomeIcon icon={faArrowRight} />
              </HMSButton>
            )}
          </div>
        </>
      )}

      {notice && <p className="rcb-notice" role="status">{notice}</p>}
      {error && !creating && <p className="rcb-error" role="alert">{error}</p>}

      <CreateContractModal
        isOpen={creating}
        busy={busy === 'create'}
        error={creating ? error : ''}
        onClose={() => setCreating(false)}
        onCreate={handleCreate}
      />
    </div>
  );
};

RecapContractBlock.propTypes = {
  recapId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  summary: PropTypes.object,
  onContractNumber: PropTypes.func,
};

export default RecapContractBlock;
